import type { AppContext } from "./context";
import type { Take } from "../core/types";
import { AppError } from "../core/errors";
import { renderTrim, type Segment } from "../edit/trim";
import { takeBlob, saveTake, formatBytes } from "../store/takes";
import { FileSink, folderPermission, getSaveFolder } from "../store/destination";
import { formatClock, on, toast } from "./dom";
import { spanBox } from "../edit/timeline";

interface Cut {
  start: number;
  end: number;
}

const MIN_SPAN = 0.5;

/** m:ss.d — the zoom readout needs tenths once the window is short. */
function formatFine(seconds: number): string {
  const whole = Math.max(0, seconds);
  const minutes = Math.floor(whole / 60);
  const rest = whole - minutes * 60;
  return `${minutes}:${rest.toFixed(1).padStart(4, "0")}`;
}

/** Trim the ends, cut sections out, save as a new take. Original untouched. */
export async function openEditor(ctx: AppContext, take: Take): Promise<void> {
  if (take.mime && !take.mime.includes("mp4")) {
    toast("Editing needs an MP4 take — this browser fell back to another format.", "error");
    return;
  }
  const dialog = document.createElement("dialog");
  dialog.className = "dialog editor";

  const title = document.createElement("h2");
  title.textContent = `Edit “${take.name}”`;

  const player = document.createElement("video");
  player.controls = true;
  player.preload = "metadata";
  player.className = "editor-player";

  const timeline = document.createElement("div");
  timeline.className = "tl";
  timeline.innerHTML = `
    <div class="tl-overview" title="Drag to move the zoomed window">
      <div class="tl-kept"></div>
      <div class="tl-cuts"></div>
      <div class="tl-window"></div>
    </div>
    <div class="tl-track">
      <div class="tl-kept"></div>
      <div class="tl-cuts"></div>
      <div class="tl-selection"><span class="tl-edge l" data-side="start"></span><span class="tl-edge r" data-side="end"></span></div>
      <div class="tl-head"></div>
    </div>`;

  const hint = document.createElement("p");
  hint.className = "fine tl-hint";
  hint.innerHTML =
    '<span class="sw sw-kept"></span>kept ' +
    '<span class="sw sw-cut"></span>cut ' +
    '<span class="sw sw-out"></span>trimmed off — drag the bar to mark a section, click it to seek.';

  const zoomRow = document.createElement("div");
  zoomRow.className = "tl-zoom";
  const zoomOut = document.createElement("button");
  zoomOut.type = "button";
  zoomOut.className = "btn small ghost";
  zoomOut.textContent = "−";
  zoomOut.title = "Show more of the take";
  const zoomLabel = document.createElement("span");
  zoomLabel.className = "mono tl-zoom-label";
  const zoomIn = document.createElement("button");
  zoomIn.type = "button";
  zoomIn.className = "btn small ghost";
  zoomIn.textContent = "+";
  zoomIn.title = "Zoom in on the timeline";
  const zoomFit = document.createElement("button");
  zoomFit.type = "button";
  zoomFit.className = "btn small ghost";
  zoomFit.textContent = "Whole take";
  zoomRow.append(zoomOut, zoomLabel, zoomIn, zoomFit);

  const controls = document.createElement("div");
  controls.className = "editor-controls";

  const startRow = document.createElement("label");
  startRow.className = "row";
  const startLabel = document.createElement("span");
  startLabel.className = "row-label";
  startLabel.textContent = "Start";
  const startInput = document.createElement("input");
  startInput.type = "range";
  startInput.min = "0";
  startInput.step = "0.05";
  startInput.value = "0";
  const startOut = document.createElement("output");
  startOut.className = "mono";
  startRow.append(startLabel, startInput, startOut);

  const endRow = document.createElement("label");
  endRow.className = "row";
  const endLabel = document.createElement("span");
  endLabel.className = "row-label";
  endLabel.textContent = "End";
  const endInput = document.createElement("input");
  endInput.type = "range";
  endInput.min = "0";
  endInput.step = "0.05";
  const endOut = document.createElement("output");
  endOut.className = "mono";
  endRow.append(endLabel, endInput, endOut);
  controls.append(startRow, endRow);

  const cutRow = document.createElement("div");
  cutRow.className = "editor-cuts";
  const cutButton = document.createElement("button");
  cutButton.type = "button";
  cutButton.className = "btn small";
  cutButton.textContent = "Cut selection";
  cutButton.disabled = true;
  const clearButton = document.createElement("button");
  clearButton.type = "button";
  clearButton.className = "btn small ghost";
  clearButton.textContent = "Clear cuts";
  const cutList = document.createElement("div");
  cutList.className = "cut-chips";
  cutRow.append(cutButton, clearButton, cutList);

  const status = document.createElement("p");
  status.className = "fine editor-status";
  const progress = document.createElement("progress");
  progress.className = "editor-progress";
  progress.max = 1;
  progress.value = 0;
  progress.hidden = true;

  const actions = document.createElement("div");
  actions.className = "dialog-actions";
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "btn ghost";
  cancel.textContent = "Cancel";
  const save = document.createElement("button");
  save.type = "button";
  save.className = "btn";
  save.textContent = "Save as new take";
  actions.append(cancel, save);

  dialog.append(title, player, timeline, hint, zoomRow, controls, cutRow, status, progress, actions);

  let duration = Math.max(0.1, take.durationMs / 1000);
  let cuts: Cut[] = [];
  let selection: Cut | null = null;
  let trimStart = 0;
  let trimEnd = duration;
  let viewStart = 0;
  let viewEnd = duration;
  let busy = false;

  const overview = timeline.querySelector<HTMLDivElement>(".tl-overview")!;
  const windowBar = timeline.querySelector<HTMLDivElement>(".tl-window")!;
  const track = timeline.querySelector<HTMLDivElement>(".tl-track")!;
  const keptBar = track.querySelector<HTMLDivElement>(".tl-kept")!;
  const cutsBar = track.querySelector<HTMLDivElement>(".tl-cuts")!;
  const selectionBar = track.querySelector<HTMLDivElement>(".tl-selection")!;
  const head = track.querySelector<HTMLDivElement>(".tl-head")!;
  const overviewKept = overview.querySelector<HTMLDivElement>(".tl-kept")!;
  const overviewCuts = overview.querySelector<HTMLDivElement>(".tl-cuts")!;

  const span = (): number => Math.max(MIN_SPAN / 100, viewEnd - viewStart);

  /** Fraction of the whole take, used by the overview strip. */
  const wholePct = (time: number): string =>
    `${Math.max(0, Math.min(100, (time / duration) * 100))}%`;

  /** Fraction of the visible window, used by the detail track. */
  const viewPct = (time: number): string =>
    `${Math.max(0, Math.min(100, ((time - viewStart) / span()) * 100))}%`;

  /** A bar for [start, end] clipped to the visible window. */
  const clip = (start: number, end: number): { left: string; width: string } => {
    const box = spanBox(start, end, viewStart, viewEnd);
    return { left: `${box.left}%`, width: `${box.width}%` };
  };

  function keptSegments(): Segment[] {
    const kept: Segment[] = [];
    let cursor = trimStart;
    for (const cut of [...cuts].sort((a, b) => a.start - b.start)) {
      const start = Math.max(trimStart, cut.start);
      const end = Math.min(trimEnd, cut.end);
      if (end <= start) continue;
      if (start > cursor) kept.push({ start: cursor, end: start });
      cursor = Math.max(cursor, end);
    }
    if (cursor < trimEnd) kept.push({ start: cursor, end: trimEnd });
    return kept;
  }

  function paint(): void {
    // Detail track: only what is inside the visible window.
    const kept = clip(trimStart, trimEnd);
    keptBar.style.left = kept.left;
    keptBar.style.width = kept.width;
    cutsBar.replaceChildren();
    cuts.forEach((cut, index) => {
      const box = clip(cut.start, cut.end);
      if (parseFloat(box.width) <= 0) return;
      const chip = document.createElement("span");
      chip.className = "tl-cut";
      chip.style.left = box.left;
      chip.style.width = box.width;
      chip.innerHTML =
        `<span class="tl-edge l" data-side="start" data-cut="${index}"></span>` +
        `<span class="tl-edge r" data-side="end" data-cut="${index}"></span>`;
      cutsBar.append(chip);
    });
    if (selection) {
      selectionBar.hidden = false;
      const box = clip(selection.start, selection.end);
      selectionBar.style.left = box.left;
      selectionBar.style.width = box.width;
    } else {
      selectionBar.hidden = true;
    }
    head.style.left = viewPct(player.currentTime || 0);

    // Overview strip: the whole take, plus the zoomed window.
    overviewKept.style.left = wholePct(trimStart);
    overviewKept.style.width = `${Math.max(0, ((Math.max(trimStart, trimEnd) - trimStart) / duration) * 100)}%`;
    overviewCuts.replaceChildren();
    for (const cut of cuts) {
      const chip = document.createElement("span");
      chip.className = "tl-cut";
      chip.style.left = wholePct(cut.start);
      chip.style.width = `${Math.max(0, ((Math.max(cut.start, cut.end) - cut.start) / duration) * 100)}%`;
      overviewCuts.append(chip);
    }
    windowBar.style.left = wholePct(viewStart);
    windowBar.style.width = `${Math.min(100, ((viewEnd - viewStart) / duration) * 100)}%`;

    const visible = viewEnd - viewStart;
    zoomLabel.textContent =
      visible >= duration - 0.01
        ? `whole take · ${formatClock(duration * 1000)}`
        : visible < 10
          ? `${formatFine(viewStart)}–${formatFine(viewEnd)} · ${visible.toFixed(1)}s`
          : `${formatClock(viewStart * 1000)}–${formatClock(viewEnd * 1000)}`;
    zoomOut.disabled = viewEnd - viewStart >= duration - 0.01;
    zoomIn.disabled = viewEnd - viewStart <= MIN_SPAN * 1.01;

    startOut.textContent = formatClock(trimStart * 1000);
    endOut.textContent = formatClock(trimEnd * 1000);
    const remaining = keptSegments().reduce((sum, segment) => sum + segment.end - segment.start, 0);
    const selectionText = selection
      ? ` · selection ${formatFine(selection.start)}–${formatFine(selection.end)} (${(selection.end - selection.start).toFixed(2)}s)`
      : "";
    status.textContent = `${cuts.length} cut${cuts.length === 1 ? "" : "s"} · result ${formatClock(remaining * 1000)}${selectionText}`;
    save.disabled = busy || remaining < 0.1;
    cutButton.disabled = !selection || selection.end - selection.start < MIN_CUT - 1e-6;
  }

  function syncRanges(): void {
    startInput.max = String(duration);
    endInput.max = String(duration);
    endInput.value = String(trimEnd);
    startInput.value = String(trimStart);
  }

  function setWindow(start: number, end: number): void {
    const width = Math.min(duration, Math.max(MIN_SPAN, end - start));
    let left = Math.max(0, Math.min(duration - width, start));
    if (!Number.isFinite(left)) left = 0;
    viewStart = left;
    viewEnd = left + width;
    paint();
  }

  /** Zooms by a factor, keeping `focus` (or the selection/playhead) in place. */
  function zoomBy(factor: number, focus?: number): void {
    const width = span();
    const target = Math.min(duration, Math.max(MIN_SPAN, width * factor));
    if (Math.abs(target - width) < 1e-6) return;
    const anchor = focus ?? (selection ? (selection.start + selection.end) / 2 : player.currentTime || viewStart + width / 2);
    const ratio = Math.max(0, Math.min(1, (anchor - viewStart) / width));
    setWindow(anchor - target * ratio, anchor + target * (1 - ratio));
  }

  // ------------------------------------------------------------ detail drag
  const MIN_CUT = 0.05;
  /** Pixels around an edge that still count as grabbing it. */
  const EDGE_TOLERANCE_PX = 14;
  type Side = "start" | "end";
  type EdgeRef = { kind: "selection" } | { kind: "cut"; index: number };
  type Drag =
    | { kind: "select"; anchor: number }
    | { kind: "edge"; ref: EdgeRef; side: Side }
    | { kind: "move"; ref: EdgeRef; grab: number; origin: { start: number; end: number } };
  let drag: Drag | null = null;
  // Survives the pointerup so arrow keys can keep nudging the edge just used.
  let lastEdge: { ref: EdgeRef; side: Side } | null = null;

  const timeAt = (clientX: number): number => {
    const rect = track.getBoundingClientRect();
    return Math.max(viewStart, Math.min(viewEnd, viewStart + ((clientX - rect.left) / rect.width) * span()));
  };

  /**
   * The edge closest to `time`, within the grab tolerance. Grips are 11px wide,
   * but a near miss must still resize — grabbing "the side" should never mean
   * hitting it to the pixel.
   */
  function nearestEdge(time: number): { ref: EdgeRef; side: Side } | null {
    const rect = track.getBoundingClientRect();
    const tolerance = (EDGE_TOLERANCE_PX / Math.max(1, rect.width)) * span();
    const candidates: { ref: EdgeRef; side: Side; at: number }[] = [];
    if (selection) {
      candidates.push({ ref: { kind: "selection" }, side: "start", at: selection.start });
      candidates.push({ ref: { kind: "selection" }, side: "end", at: selection.end });
    }
    cuts.forEach((cut, index) => {
      candidates.push({ ref: { kind: "cut", index }, side: "start", at: cut.start });
      candidates.push({ ref: { kind: "cut", index }, side: "end", at: cut.end });
    });
    let best: { ref: EdgeRef; side: Side } | null = null;
    let bestDistance = Infinity;
    for (const candidate of candidates) {
      const distance = Math.abs(candidate.at - time);
      if (distance > tolerance || distance >= bestDistance) continue;
      bestDistance = distance;
      best = { ref: candidate.ref, side: candidate.side };
    }
    return best;
  }

  function nudgeEdge(seconds: number): void {
    if (!lastEdge) return;
    if (lastEdge.ref.kind === "selection" && selection) {
      if (lastEdge.side === "start") selection.start = Math.max(0, Math.min(selection.start + seconds, selection.end - MIN_CUT));
      else selection.end = Math.min(duration, Math.max(selection.end + seconds, selection.start + MIN_CUT));
    } else if (lastEdge.ref.kind === "cut") {
      const cut = cuts[lastEdge.ref.index];
      if (!cut) return;
      if (lastEdge.side === "start") cut.start = Math.max(0, Math.min(cut.start + seconds, cut.end - MIN_CUT));
      else cut.end = Math.min(duration, Math.max(cut.end + seconds, cut.start + MIN_CUT));
      renderChips();
    }
    paint();
  }

  on(track, "pointerdown", (event) => {
    if (busy) return;
    const time = timeAt(event.clientX);
    track.setPointerCapture(event.pointerId);

    const edge = nearestEdge(time);
    if (edge) {
      drag = { kind: "edge", ref: edge.ref, side: edge.side };
      lastEdge = edge;
      paint();
      return;
    }

    // Inside the selection or an existing cut: slide it instead of wiping it.
    if (selection && time >= selection.start && time <= selection.end) {
      drag = { kind: "move", ref: { kind: "selection" }, grab: time, origin: { start: selection.start, end: selection.end } };
      lastEdge = null;
      return;
    }
    const hit = cuts.findIndex((cut) => time >= cut.start && time <= cut.end);
    if (hit >= 0) {
      const cut = cuts[hit]!;
      drag = { kind: "move", ref: { kind: "cut", index: hit }, grab: time, origin: { start: cut.start, end: cut.end } };
      lastEdge = null;
      return;
    }

    drag = { kind: "select", anchor: time };
    lastEdge = null;
    selection = { start: time, end: time };
    paint();
  });

  on(track, "pointermove", (event) => {
    if (!drag) return;
    const time = timeAt(event.clientX);

    if (drag.kind === "edge") {
      if (drag.ref.kind === "selection" && selection) {
        if (drag.side === "start") selection.start = Math.min(time, selection.end - MIN_CUT);
        else selection.end = Math.max(time, selection.start + MIN_CUT);
      } else if (drag.ref.kind === "cut") {
        const cut = cuts[drag.ref.index];
        if (!cut) return;
        if (drag.side === "start") cut.start = Math.min(time, cut.end - MIN_CUT);
        else cut.end = Math.max(time, cut.start + MIN_CUT);
        renderChips();
      }
      paint();
      return;
    }

    if (drag.kind === "move") {
      const width = drag.origin.end - drag.origin.start;
      const start = Math.max(0, Math.min(Math.max(0, duration - width), drag.origin.start + (time - drag.grab)));
      if (drag.ref.kind === "selection" && selection) {
        selection.start = start;
        selection.end = start + width;
      } else if (drag.ref.kind === "cut") {
        const cut = cuts[drag.ref.index];
        if (!cut) return;
        cut.start = start;
        cut.end = start + width;
        renderChips();
      }
      paint();
      return;
    }

    selection = { start: Math.min(drag.anchor, time), end: Math.max(drag.anchor, time) };
    paint();
  });

  const endDrag = (event: PointerEvent) => {
    const wasSelect = drag?.kind === "select";
    drag = null;
    if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
    if (wasSelect && selection && selection.end - selection.start < 0.02) {
      // A click, not a drag: seek there and drop the empty selection.
      player.currentTime = selection.start;
      selection = null;
    }
    paint();
  };
  on(track, "pointerup", endDrag);
  on(track, "pointercancel", () => {
    drag = null;
    paint();
  });

  track.tabIndex = 0;
  // Listens on the dialog: a modal traps focus, so the track rarely holds it.
  on(dialog, "keydown", (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    const target = event.target as HTMLElement | null;
    if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) return;
    if (!lastEdge) return;
    event.preventDefault();
    const step = (event.shiftKey ? 0.5 : 0.02) * (event.key === "ArrowLeft" ? -1 : 1);
    nudgeEdge(step);
  });

  on(track, "wheel", (event) => {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    const rect = track.getBoundingClientRect();
    const focus = viewStart + Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)) * span();
    zoomBy(event.deltaY > 0 ? 2 : 0.5, focus);
  });

  // -------------------------------------------------------- overview window
  let panFrom: { x: number; start: number } | null = null;
  on(overview, "pointerdown", (event) => {
    if (busy) return;
    const rect = overview.getBoundingClientRect();
    const at = ((event.clientX - rect.left) / rect.width) * duration;
    const width = span();
    const inside = at >= viewStart && at <= viewEnd;
    if (!inside && width < duration) {
      // Jump the window to where they clicked, then let the drag pan it.
      setWindow(at - width / 2, at + width / 2);
    }
    panFrom = { x: event.clientX, start: viewStart };
    overview.setPointerCapture(event.pointerId);
  });
  on(overview, "pointermove", (event) => {
    if (!panFrom) return;
    const rect = overview.getBoundingClientRect();
    const delta = ((event.clientX - panFrom.x) / rect.width) * duration;
    setWindow(panFrom.start + delta, panFrom.start + delta + span());
  });
  on(overview, "pointerup", (event) => {
    if (!panFrom) return;
    panFrom = null;
    overview.releasePointerCapture(event.pointerId);
  });

  on(zoomIn, "click", () => zoomBy(0.5));
  on(zoomOut, "click", () => zoomBy(2));
  on(zoomFit, "click", () => setWindow(0, duration));

  on(startInput, "input", () => {
    trimStart = Math.min(Number(startInput.value), trimEnd - 0.1);
    startInput.value = String(trimStart);
    player.currentTime = trimStart;
    if (trimStart < viewStart || trimStart > viewEnd) setWindow(trimStart - span() / 2, trimStart + span() / 2);
    paint();
  });
  on(endInput, "input", () => {
    trimEnd = Math.max(Number(endInput.value), trimStart + 0.1);
    endInput.value = String(trimEnd);
    if (trimEnd > viewEnd || trimEnd < viewStart) setWindow(trimEnd - span() / 2, trimEnd + span() / 2);
    paint();
  });

  on(cutButton, "click", () => {
    if (!selection || selection.end - selection.start < MIN_CUT - 1e-6) return;
    cuts.push({ ...selection });
    lastEdge = { ref: { kind: "cut", index: cuts.length - 1 }, side: "end" };
    selection = null;
    paint();
    renderChips();
  });
  on(clearButton, "click", () => {
    cuts = [];
    selection = null;
    paint();
    renderChips();
  });

  function renderChips(): void {
    cutList.replaceChildren();
    cuts.forEach((cut, index) => {
      const chip = document.createElement("span");
      chip.className = "cut-chip";
      chip.textContent = `${cut.start.toFixed(2)}s–${cut.end.toFixed(2)}s`;
      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "cut-remove";
      remove.textContent = "×";
      remove.setAttribute("aria-label", "Remove this cut");
      on(remove, "click", () => {
        cuts.splice(index, 1);
        paint();
        renderChips();
      });
      chip.append(remove);
      cutList.append(chip);
    });
  }

  on(player, "timeupdate", () => {
    head.style.left = viewPct(player.currentTime || 0);
    // Follow the playhead when it leaves the zoomed window.
    if (player.currentTime < viewStart || player.currentTime > viewEnd) {
      setWindow(player.currentTime - span() / 2, player.currentTime + span() / 2);
    }
  });

  on(cancel, "click", () => dialog.close());
  on(dialog, "close", () => dialog.remove());

  // ------------------------------------------------------------------- save
  on(save, "click", async () => {
    if (busy) return;
    const segments = keptSegments();
    if (segments.length === 0) return;
    busy = true;
    save.disabled = true;
    cancel.disabled = true;
    progress.hidden = false;
    status.textContent = "Starting…";
    try {
      const source = await takeBlob(take);
      const result = await Promise.race([
        renderTrim(source, segments, (fraction, label) => {
          progress.value = fraction;
          status.textContent = label;
        }),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("The trim took too long — the file may be damaged.")), 180_000),
        ),
      ]);

      status.textContent = "Saving…";
      const folder = ctx.session.saveFolder ?? (await getSaveFolder());
      if (!folder || (await folderPermission(folder)) !== "granted") {
        throw new AppError("no-folder", "The save folder needs permission again.");
      }
      const fileName = `${take.fileName.replace(/\.[^.]+$/, "")} (edited).mp4`;
      const sink = await FileSink.open(folder.handle, fileName);
      await sink.write(result.blob);
      const bytes = await sink.close();

      const edited: Take = {
        id: crypto.randomUUID(),
        name: fileName.replace(/\.[^.]+$/, ""),
        fileName: sink.fileName,
        mime: result.mime,
        bytes,
        durationMs: result.durationMs,
        width: take.width,
        height: take.height,
        createdAt: Date.now(),
        thumb: await thumbnailOf(result.blob),
        folderName: folder.name,
        fileHandle: sink.fileHandle,
        recovered: false,
      };
      await saveTake(edited);
      await ctx.refreshLibrary();
      toast(`Saved ${edited.fileName} · ${formatBytes(bytes)}`, "ok");
      dialog.close();
    } catch (error) {
      const message = error instanceof AppError ? error.message : error instanceof Error ? error.message : String(error);
      status.textContent = message;
      toast(message, "error");
      console.error(error);
    } finally {
      busy = false;
      cancel.disabled = false;
      save.disabled = keptSegments().length === 0;
      // No paint() here: it would overwrite an error message with the summary.
    }
  });

  document.body.append(dialog);
  dialog.showModal();

  // The player needs a source; read the file once, then reuse the blob.
  try {
    const blob = await takeBlob(take);
    const url = URL.createObjectURL(blob);
    player.src = url;
    dialog.addEventListener("close", () => URL.revokeObjectURL(url));
    player.addEventListener(
      "loadedmetadata",
      () => {
        if (Number.isFinite(player.duration) && player.duration > 0) {
          duration = player.duration;
          trimStart = 0;
          trimEnd = duration;
          setWindow(0, duration);
          syncRanges();
          paint();
        }
      },
      { once: true },
    );
  } catch {
    toast("The file needs folder permission again.", "error");
    dialog.close();
  }

  syncRanges();
  paint();
  renderChips();
}

async function thumbnailOf(blob: Blob): Promise<string | null> {
  const url = URL.createObjectURL(blob);
  const video = document.createElement("video");
  video.muted = true;
  video.src = url;
  const gate = Promise.withResolvers<void>();
  video.addEventListener("loadeddata", () => gate.resolve(), { once: true });
  video.addEventListener("error", () => gate.resolve(), { once: true });
  setTimeout(() => gate.resolve(), 5000);
  await gate.promise;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 320;
    canvas.height = Math.max(1, Math.round((320 * (video.videoHeight || 9)) / (video.videoWidth || 16)));
    canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.7);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}
